/* eslint-disable @typescript-eslint/no-explicit-any */

import apiService from 'services/api'
import { Tag } from 'models/tags'
import { AssignModal, AssignModalProps } from './BaseAssignModal'
import { bulkEditMultiParams, bulkEditTagsParams } from 'services/apiTypes'
import { useDataTableContext } from 'components/DataTable'
import { formatInteger } from 'utils/formatting'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { TaglistCell } from '../../FbUI/StagePill'

export default function TagAssignModal({
  tableObjAccessor = 'taglist',
  tagParent = 'products',
  apiTagsAction,
  ...props
}: {
  tableObjAccessor?: string
  tagParent?: 'products' | 'contacts'
  apiTagsAction: (params: bulkEditTagsParams) => Promise<any>
} & Omit<
  AssignModalProps<Tag, any>,
  | 'apiAction'
  | 'title'
  | 'subtitle'
  | 'pluralLabel'
  | 'singularLabel'
  | 'onConfirmAddNew'
  | 'items'
  | 'tableObjAccessor'
>) {
  const api = apiService()
  const { data: tags } = api.useGetTags(tagParent)
  const {
    state: { totalSelectedRows, table, rowSelection },
  } = useDataTableContext()

  // TODO: POC, not fully implemented
  const existingTags = useMemo(() => {
    const selectedTagsInRows = table
      ?.getRowModel()
      .flatRows.filter((r, i) => rowSelection[i])
      .map((r) => (r.original as any)?.['taglist'])

    const tagIds = selectedTagsInRows
      ?.map((taglist) => {
        if (!taglist) return []
        // This is to account the different types of response for taglist attr in app
        // Sometimes it is a number[], other times a Tag[]
        if (Array.isArray(taglist?.[0])) {
          return taglist
        } else {
          return taglist?.map((tag: any) => tag.id)
        }
      })
      .flat()

    return [...new Set(tagIds)]
  }, [table, rowSelection])

  const createNewTag = async (value: string) => {
    return await api.postTag(
      {
        tag: value,
      },
      tagParent
    )
  }

  const apiAction = async (params: bulkEditMultiParams) => {
    const { items_ids: itemsIds, ...rest } = params
    return await apiTagsAction({
      ...rest,
      taglist: itemsIds,
    })
  }

  if (!tags) return null

  const title = `Assign ${tagParent === 'products' ? 'Tags' : 'Contact Labels'}`
  const subtitle = (
    <span>
      Select {tagParent === 'products' ? 'tags' : 'contact labels'} to apply or
      remove from <b>{formatInteger(totalSelectedRows)}</b> selected row
      {totalSelectedRows > 1 ? 's' : ''}.
    </span>
  )
  const pluralLabel = tagParent === 'products' ? 'Tags' : 'Contact Labels'
  const singularLabel = tagParent === 'products' ? 'Tag' : 'Contact Label'

  return (
    <AssignModal
      {...props}
      title={title}
      subtitle={subtitle}
      pluralLabel={pluralLabel}
      singularLabel={singularLabel}
      onConfirmAddNew={createNewTag}
      tableObjAccessor={tableObjAccessor!}
      apiAction={apiAction}
      items={tags}
      columns={[
        {
          accessorKey: 'tag',
          header: 'Name',
          cell: (info) => {
            return <TaglistCell taglist={[info.row.original]} />

            // This is a POC, not including in release ATM
            if (existingTags.includes(info.row.original.id)) {
              return (
                <>
                  <div
                    className={'rounded-full h-3 w-3 bg-green-500 mr-2'}
                  ></div>
                  {info.getValue()}
                </>
              )
            } else {
              return info.getValue()
            }
          },
        },
        {
          accessorKey: 'created',
          header: 'Date Created',
          cell: (info) => dayjs(info.getValue()).format('MMM DD YYYY'),
          size: 100,
        },
        {
          accessorKey: tagParent === 'products' ? 'opp_count' : 'contact_count',
          header:
            tagParent === 'products' ? 'Unique Companies' : 'Unique Contacts',
          meta: { rightAlign: true },
          size: 100,
        },
      ]}
    />
  )
}
